// TrialDemo.js
import React, { useState, useEffect } from 'react';
import {
  View,
  ActivityIndicator,
  StyleSheet,
  Platform,
  Text,
  TouchableOpacity,
} from 'react-native';

// Load endpoints and IDs from .env
import {
  BASE_URL,
  LOGIN_API,
  USER_API,
  TOKEN_GENERATION_API,
  CURRICULUM_ID,
  UNIT_ID,
  AVATAR_URL,
  METAVERSE_BASE_URL,
} from '@env';

const LOGIN_URL    = `${BASE_URL}${LOGIN_API}`;
const USER_URL     = `${BASE_URL}${USER_API}`;
const GENERATE_URL = `${BASE_URL}${TOKEN_GENERATION_API}`;

// 3 hours in milliseconds
const FRONTEND_WINDOW_MS = 3 * 60 * 60 * 1000;

/** Send meta-token request and redirect */
async function handleMetaverse(token, userId, showTrialDemo, showLVDemo) {
  const res = await fetch(GENERATE_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      userId,
      curriculumId: CURRICULUM_ID,
      unitId:      UNIT_ID,
    }),
  });
  if (!res.ok) throw new Error(`MetaGen HTTP ${res.status}`);
  const { token: metaToken } = await res.json();

  const params = new URLSearchParams({
    token:           metaToken,
    avatarSelection: 'false',
    legacy:          'false',
    avatarUrl:       AVATAR_URL,
    showTrialDemo:   showTrialDemo,
    showLVDemo:   showLVDemo,
  });
  window.location.href = `${METAVERSE_BASE_URL}/?${params}`;
}

export default function TrialDemo({ loading, showTrialDemo, showLVDemo }) {
  const [done,    setDone]    = useState(false);
  const [error,   setError]   = useState(false);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!loading) return;

    // Reset UI state on each mount
    setDone(false);
    setError(false);
    setExpired(false);

    (async () => {
      try {
        const now = Date.now();
        let firstLoginAt = parseInt(localStorage.getItem('firstLoginAt') || '0', 10);

        // If this is the very first time, record it
        if (!firstLoginAt) {
          firstLoginAt = now;
          localStorage.setItem('firstLoginAt', String(firstLoginAt));
        }

        // // Absolute trial expiry check
        // if (now - firstLoginAt >= FRONTEND_WINDOW_MS) {
        //   setExpired(true);
        //   return;
        // }

        // Always log in again (i.e. “log out” then fresh login)
        const lRes = await fetch(LOGIN_URL, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email:    'guest.user.1@gmail.com',
            password: 'GuestPassword1!',
          }),
        });
        if (!lRes.ok) throw new Error(`Login HTTP ${lRes.status}`);
        const {
          AccessToken,
          ExpiresIn, // we ignore ExpiresIn for frontend-window purposes
        } = (await lRes.json()).data.AuthenticationResult;

        // Store new token + sliding frontend expiry
        const tokenExpiry = now + FRONTEND_WINDOW_MS;
        localStorage.setItem('accessToken', AccessToken);
        localStorage.setItem('tokenExpiry', String(tokenExpiry));

        // Fetch user profile then redirect
        const uRes = await fetch(USER_URL, {
          headers: { Authorization: `Bearer ${AccessToken}` },
        });
        if (!uRes.ok) throw new Error(`User HTTP ${uRes.status}`);
        const { data } = await uRes.json();

        await handleMetaverse(AccessToken, data.userId, showTrialDemo, showLVDemo);

      } catch (err) {
        console.error(err);
        // // If backend explicitly says “unauthorized”, treat as trial over
        // if (err.message.includes('HTTP 401')) {
        //   setExpired(true);
        // } else {
           setError(true);
        // }
      } finally {
        setDone(true);
      }
    })();
  }, [loading]);

  return (
    <View style={styles.fullScreen}>
      {!done && (
        <View style={styles.inner}>
          <ActivityIndicator size="large" color="rgb(195,232,55)" />
        </View>
      )}

      {done && error && (
        <View style={styles.errorBox}>
          <Text style={styles.errorText}>
            Trial is not available currently. Please try again later.
          </Text>
          <TouchableOpacity
            style={styles.errorButton}
            onPress={() => window.location.reload()}
          >
            <Text style={styles.errorButtonText}>Retry</Text>
          </TouchableOpacity>
        </View>
      )}

      {done && !error && expired && (
        <View style={styles.errorBox}>
          <Text style={styles.errorText}>
            Your Trial is Over. Please go to Home or Subscribe to Quiklearn to keep using it.
          </Text>
          <View style={styles.buttonRow}>
            <TouchableOpacity
              style={styles.errorButton}
              onPress={() => (window.location.href = 'https://quiklearn.ai')}
            >
              <Text style={styles.errorButtonText}>Home Page</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.errorButton}
              onPress={() => (window.location.href = 'https://web.quiklearn.ai/quiksignup')}
            >
              <Text style={styles.errorButtonText}>Subscribe</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  fullScreen: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: '#000',
    alignItems:      'center',
    justifyContent:  'center',
    zIndex:          9999,
    elevation:       9999,
  },
  inner: {
    backgroundColor: '#000',
    padding:         20,
    borderRadius:    8,
    ...Platform.select({
      ios: {
        shadowColor:   '#000',
        shadowOpacity: 0.3,
        shadowRadius:  5,
        shadowOffset:  { width: 0, height: 2 },
      },
      android: { elevation: 5 },
      default: {},
    }),
  },
  errorBox: {
    backgroundColor: '#000',
    borderColor:     'rgba(189,222,54,0.3)',
    borderWidth:     1,
    borderRadius:    8,
    padding:         20,
    minWidth:        350,
    alignItems:      'center',
    shadowColor:     'rgba(197,232,55,1)',
    shadowOffset:    { width: 0, height: 0 },
    shadowOpacity:   0.35,
    shadowRadius:    15,
    elevation:       20,
  },
  errorText: {
    color:        '#fff',
    fontSize:     16,
    marginBottom: 20,
    textAlign:    'center',
  },
  errorButton: {
    backgroundColor: 'rgba(189,222,54,0.1)',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius:     8,
    marginHorizontal: 5,
  },
  errorButtonText: {
    color:      '#BDDE36',
    fontSize:   14,
    fontWeight: '500',
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
});
